import {HubConnection, HubConnectionBuilder, HubConnectionState} from "@microsoft/signalr";
import {getAccessToken} from "./auth.service";
import { IndividualAiEstimatedCostGenerationStatus } from "../dtos/estimate-tree-operation.dto";

// This interface needs to be kept in sync with IClientSupportedInboundMessages in RealTimeHub.cs in the API
export interface SupportedInboundMessageRegistrations {
  onMeasuredTreeImageCreated(callback: (estimateId: number, treeId: number, storedImageId: number, s3Url: string, widthFeet: number, heightFeet: number) => void): void;
  onMeasuredTrunkImageCreated(callback: (estimateId: number, treeId: number, storedImageId: number, s3Url: string, diameterInches: number) => void): void;
  onAiEstimatedCostStatusUpdated(callback: (estimateId: number, treeId: number, treeOperationId: number, status: IndividualAiEstimatedCostGenerationStatus, newAiEstimatedCost?: number) => void): void;
  // SQ-559: a job's crew or crew clocks changed (sent to the whole company).
  onJobCrewChanged(callback: (workOrderId: number, estimateId: number) => void): void;
  // SQ-745: an estimate's status changed, the customer's approval from their link among others (sent to the whole company).
  onEstimateStatusChanged(callback: (estimateId: number) => void): void;
}

class RealTimeService implements SupportedInboundMessageRegistrations {
  private static instance: RealTimeService;
  private connection: HubConnection | undefined;

  private onMeasuredTreeImageCreatedCallback: (estimateId: number, treeId: number, storedImageId: number, s3Url: string, widthFeet: number, heightFeet: number) => void = () => {};
  private onMeasuredTrunkImageCreatedCallback: (estimateId: number, treeId: number, storedImageId: number, s3Url: string, diameterInches: number) => void = () => {};
  private onAiEstimatedCostStatusUpdatedCallback: (estimateId: number, treeId: number, treeOperationId: number, status: IndividualAiEstimatedCostGenerationStatus, newAiEstimatedCost?: number) => void = () => {};
  private onJobCrewChangedCallback: (workOrderId: number, estimateId: number) => void = () => {};
  private onEstimateStatusChangedCallback: (estimateId: number) => void = () => {};

  private constructor() {
    try {
      console.log('Creating RealTimeService');
      this.connection = new HubConnectionBuilder()
        .withUrl(process.env.REACT_APP_ACCUSITE_API_BASE_URL + '/real-time', {
          // Resolved on each (re)connect, so SignalR always gets a fresh,
          // Firebase-refreshed token rather than a stale snapshot.
          accessTokenFactory: async () => (await getAccessToken()) ?? ''
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000])
        .build();
      console.log('Created RealTimeService');

      this.connection.on('MeasuredTreeImageCreated', (estimateId: number, treeId: number, storedImageId: number, s3Url: string, widthFeet: number, heightFeet: number) => {
        this.onMeasuredTreeImageCreatedCallback(estimateId, treeId, storedImageId, s3Url, widthFeet, heightFeet);
      });

      this.connection.on('MeasuredTrunkImageCreated', (estimateId: number, treeId: number, storedImageId: number, s3Url: string, diameterInches: number) => {
        this.onMeasuredTrunkImageCreatedCallback(estimateId, treeId, storedImageId, s3Url, diameterInches);
      });

      this.connection.on('AiEstimatedCostStatusUpdated', (estimateId: number, treeId: number, treeOperationId: number, status: IndividualAiEstimatedCostGenerationStatus, newAiEstimatedCost?: number) => {
        this.onAiEstimatedCostStatusUpdatedCallback(estimateId, treeId, treeOperationId, status, newAiEstimatedCost);
      });

      this.connection.on('JobCrewChanged', (workOrderId: number, estimateId: number) => {
        this.onJobCrewChangedCallback(workOrderId, estimateId);
      });

      this.connection.on('EstimateStatusChanged', (estimateId: number) => {
        this.onEstimateStatusChangedCallback(estimateId);
      });

      this.startConnection(this.connection);
    }
    catch (e) {
      console.error('Error creating RealTimeService', e);
    }
  }

  public static getInstance(): RealTimeService {
    const newInstanceNeeded = !RealTimeService.instance?.isConnected() &&  !RealTimeService.instance?.isConnecting()
    console.log('RealTimeService.getInstance', RealTimeService.instance?.isConnected(), RealTimeService.instance?.isConnecting(), newInstanceNeeded);
    if (newInstanceNeeded) {
      RealTimeService.instance = new RealTimeService();
    }
    return RealTimeService.instance;
  }

  private startConnection(connection: HubConnection) {
    if (connection.state === HubConnectionState.Disconnected) {
      connection.start()
        .then(() => {
          console.log(`Connection started, state: ${connection.state}`);
        })
        .catch(e => {
          console.log('Connection failed: ', e);
        });

      connection.onclose(error => {
        console.error("Connection lost:", error);
      });
    }
  }

  public isConnected(): boolean {
    return this.connection?.state === HubConnectionState.Connected;
  }

  public isConnecting(): boolean {
    return this.connection?.state === HubConnectionState.Connecting;
  }

  public sendMessage(user: string, message: string) {
    if(!this.connection) {
      console.error('Connection not established');
      return;
    }
    this.connection.invoke('SendMessage', user, message)
      .then(() => console.log('Message sent'))
      .catch(err => console.error(err));
  }

  public onMeasuredTreeImageCreated(callback: (estimateId: number, treeId: number, storedImageId: number, s3Url: string, widthFeet: number, heightFeet: number) => void) {
    this.onMeasuredTreeImageCreatedCallback = callback;
  }

  public onMeasuredTrunkImageCreated(callback: (estimateId: number, treeId: number, storedImageId: number, s3Url: string, diameterInches: number) => void) {
    this.onMeasuredTrunkImageCreatedCallback = callback;
  }

  public onAiEstimatedCostStatusUpdated(callback: (estimateId: number, treeId: number, treeOperationId: number, status: IndividualAiEstimatedCostGenerationStatus, newAiEstimatedCost?: number) => void) {
    this.onAiEstimatedCostStatusUpdatedCallback = callback;
  }

  public onJobCrewChanged(callback: (workOrderId: number, estimateId: number) => void) {
    this.onJobCrewChangedCallback = callback;
  }

  public onEstimateStatusChanged(callback: (estimateId: number) => void) {
    this.onEstimateStatusChangedCallback = callback;
  }
}

export default RealTimeService;